import { useCallback, useRef, useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { RadioCard, Switch } from '@/components/ui/Toggle'
import { useLicense } from '@/features/billing/useLicense'
import type { ApplyOptions, Job } from '@/types/api'

const STORAGE_KEY = 'tsenta.applyOptions'

interface ResolvedOptions {
  tailorResume: boolean
  coverLetter: boolean
}

/** Tailoring is the recommended default; a cover letter is opt-in. */
const DEFAULTS: ResolvedOptions = { tailorResume: true, coverLetter: false }

function loadLast(): ResolvedOptions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<ResolvedOptions>
    return {
      tailorResume: typeof parsed.tailorResume === 'boolean' ? parsed.tailorResume : DEFAULTS.tailorResume,
      coverLetter: typeof parsed.coverLetter === 'boolean' ? parsed.coverLetter : DEFAULTS.coverLetter,
    }
  } catch {
    return DEFAULTS
  }
}

function saveLast(options: ResolvedOptions): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options))
  } catch {
    /* private mode — we simply ask with the defaults next time */
  }
}

type DialogJob = Pick<Job, 'title' | 'companyName' | 'source'>

interface ApplyOptionsDialogProps {
  open: boolean
  jobs: DialogJob[]
  onClose: () => void
  onConfirm: (options: ApplyOptions) => void
}

/**
 * Asked on every apply: send a resume tailored to the job, or the master
 * resume as uploaded, and whether to add a cover letter. The last answer is
 * preselected, never assumed.
 */
export function ApplyOptionsDialog({ open, jobs, onClose, onConfirm }: ApplyOptionsDialogProps) {
  if (!open || jobs.length === 0) return null
  // Mounted per opening, so each one starts from the remembered choice.
  return <DialogBody jobs={jobs} onClose={onClose} onConfirm={onConfirm} />
}

function DialogBody({ jobs, onClose, onConfirm }: Omit<ApplyOptionsDialogProps, 'open'>) {
  const [options, setOptions] = useState<ResolvedOptions>(loadLast)
  const single = jobs.length === 1 ? jobs[0]! : null
  // A plan without tailoring (Starter) sends the resume as uploaded, with one standard letter.
  const { features, subscription } = useLicense()
  const noTailoring = Boolean(features && subscription?.active && !features.resumeTailoring)
  const noLetterChoice = Boolean(features && subscription?.active && !features.coverLetterTailoring)
  const planName = subscription?.subscription?.planName ?? 'Your'
  const effective: ResolvedOptions = {
    tailorResume: noTailoring ? false : options.tailorResume,
    coverLetter: noLetterChoice ? Boolean(features?.fixedCoverLetter) : options.coverLetter,
  }
  const hasNaukri = jobs.some((job) => job.source === 'naukri')

  const title = single ? `Apply to ${single.companyName}` : `Apply to ${jobs.length} jobs`
  const description = single
    ? `${single.title}. Choose which resume goes out with this application.`
    : 'Choose which resume goes out with these applications.'
  const confirmLabel = single ? 'Apply' : `Apply to ${jobs.length} jobs`

  const confirm = () => {
    // Remember the choice made, not what the plan forced, for when tailoring is available.
    saveLast(options)
    onConfirm(effective)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button className="flex-1 sm:flex-none sm:min-w-[120px]" onClick={confirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 pb-4">
        <div role="radiogroup" aria-label="Resume to send" className="panel overflow-hidden">
          <RadioCard
            selected={effective.tailorResume}
            onSelect={() => setOptions((o) => ({ ...o, tailorResume: true }))}
            disabled={noTailoring}
            title={single ? 'Tailor my resume to this job' : 'Tailor my resume to each job'}
            badge={
              <span className="rounded-pill bg-brand-soft px-2 py-0.5 text-[11px] font-medium text-brand">
                {noTailoring ? 'Pro and Power' : 'Recommended'}
              </span>
            }
            description="Rewrites your summary, highlights and bullet order for the job description. Your core skills stay the same, and the layout stays ATS-friendly."
          />
          <RadioCard
            selected={!effective.tailorResume}
            onSelect={() => setOptions((o) => ({ ...o, tailorResume: false }))}
            title={single ? 'Use my master resume' : 'Use my master resume for all'}
            description="Sends the resume you uploaded, as is. Fastest."
            className="border-t border-line"
          />
        </div>

        {noLetterChoice ? (
          <Note>
            {features?.fixedCoverLetter
              ? `${planName} plan: your resume goes as uploaded, with one standard cover letter. Pro and Power tailor both to each job.`
              : `${planName} plan: no cover letter is written for each job.`}
          </Note>
        ) : (
        <label className="panel flex min-h-[56px] cursor-pointer items-center justify-between gap-4 px-4 py-3.5 sm:px-5">
          <span className="min-w-0">
            <span className="block text-[15px] font-medium text-ink">Also write a cover letter</span>
            <span className="mt-0.5 block text-[13px] leading-relaxed text-ink-muted">
              A short letter written for {single ? 'this role' : 'each role'}, sent where the form accepts one.
            </span>
          </span>
          <Switch
            checked={options.coverLetter}
            onChange={(value) => setOptions((o) => ({ ...o, coverLetter: value }))}
            label="Also write a cover letter"
          />
        </label>
        )}

        {effective.tailorResume && hasNaukri && (
          <Note>
            Naukri sends the resume on your profile, so we upload the tailored one just before applying and put
            yours back after. Naukri applications then go one at a time.
          </Note>
        )}
      </div>
    </Modal>
  )
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="flex gap-2.5 rounded-field bg-surface-muted px-3.5 py-3 text-[13px] leading-relaxed text-ink-soft">
      <svg className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="6.8" stroke="currentColor" strokeWidth="1.3" />
        <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="8" cy="5" r="0.8" fill="currentColor" />
      </svg>
      <span>{children}</span>
    </p>
  )
}

/**
 * Wires the dialog to a page: `ask(jobs, run)` opens it, and `run` gets the
 * chosen options only once the user confirms. Render `element` once.
 */
export function useApplyDialog() {
  const [jobs, setJobs] = useState<DialogJob[]>([])
  const pending = useRef<((options: ApplyOptions) => void) | null>(null)
  // No plan, a lapsed plan, or nothing left this month: the upgrade dialog
  // opens instead, before anything is asked or sent.
  const { requireApply } = useLicense()

  const close = useCallback(() => {
    pending.current = null
    setJobs([])
  }, [])

  const ask = useCallback(
    (targets: DialogJob[], run: (options: ApplyOptions) => void) => {
      if (targets.length === 0) return
      requireApply(() => {
        pending.current = run
        setJobs(targets)
      })
    },
    [requireApply],
  )

  const element = (
    <ApplyOptionsDialog
      open={jobs.length > 0}
      jobs={jobs}
      onClose={close}
      onConfirm={(options) => {
        const run = pending.current
        close()
        run?.(options)
      }}
    />
  )

  return { ask, element }
}
